import { useState } from 'react'
import { barWidth } from './bars'
import { formatShortDate } from '../../formatters'
import { Thumb } from './Thumb'
import styles from './Widgets.module.css'

const WHITESPACE_PATTERN = /\s+/

// Height of one row, in pixels, at the default card size: it holds a 36px
// thumbnail. A bigger card scales it (useFittedRows) up to the max, and the
// rows then share the box exactly. It lives here rather than in the
// stylesheet where the two could drift; it never depends on what a row
// contains.
export const RANK_ROW_HEIGHT = 48
export const RANK_ROW_MAX_HEIGHT = 64

// A ranked list of things with a number beside each. Two lines per row, so a
// post keeps its own title and never has its creator's name crammed onto the
// end of it. A row can lead with a picture (item.image: { src, kind }), date
// its subtitle (item.date), and draw its value as a share of the top row's
// (item.bar, 0 to 1) in a thin bar under its text.
export function RankList({ items, label, onSelect, rowHeight = RANK_ROW_HEIGHT, emptyLabel = 'Nothing to rank yet' }) {
  if (!items.length) return <p className={styles.caveat}>{emptyLabel}</p>
  const withImages = items.some((item) => item.image)
  return (
    <ol className={styles.rank} aria-label={label} data-images={withImages || undefined} style={{ '--rank-row-height': `${rowHeight}px` }}>
      {items.map((item, index) => {
        const subtitle = [item.date ? formatShortDate(item.date) : null, item.subtitle].filter(Boolean).join(' · ')
        const body = (
          <>
            <span className={styles.rankIndex}>{item.rank ?? index + 1}</span>
            {withImages ? <Thumb src={item.image?.src} kind={item.image?.kind} size="row" /> : null}
            <span className={styles.rankCopy}>
              <span className={styles.rankTitle}>{item.title}</span>
              {subtitle ? <span className={styles.rankSubtitle}>{subtitle}</span> : null}
              {item.bar == null ? null : (
                <span className={styles.rankTrack} aria-hidden="true">
                  <span className={styles.rankFill} style={{ width: barWidth(item.bar) }} />
                </span>
              )}
            </span>
            <span className={styles.rankFigures}>
              <span className={styles.rankValue} title={item.valueTitle}>{item.value}</span>
              {item.meta ? <span className={styles.rankMeta} data-tone={item.tone}>{item.meta}</span> : null}
            </span>
          </>
        )
        return (
          <li key={item.key} className={styles.rankRow}>
            {onSelect ? (
              <button type="button" className={styles.rankButton} onClick={() => onSelect(item)}>{body}</button>
            ) : <span className={styles.rankButton}>{body}</span>}
          </li>
        )
      })}
    </ol>
  )
}

// A creator's picture, or their initials when there is none or it fails to
// load. The shape is the same either way so the layout does not move.
export function CreatorAvatar({ name, src, large = false }) {
  const [failedSrc, setFailedSrc] = useState(null)
  const initials = String(name || '?')
    .split(WHITESPACE_PATTERN)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('') || '?'
  return (
    <span className={`${styles.avatar} ${large ? styles.avatarLarge : ''}`.trim()} aria-hidden="true">
      {src && failedSrc !== src ? <img className={styles.avatarImage} src={src} alt="" loading="lazy" decoding="async" onError={() => setFailedSrc(src)} /> : initials}
    </span>
  )
}
